/**
 * `WhyTabPanel` renders the content for a single "Why" tab.
 *
 * Behaviour:
 * - Uses `TabPanel` to conditionally render based on the selected tab id.
 * - Desktop: renders all `WhyTabItem` entries in a row and optionally shows a
 *   caveat/disclaimer.
 * - Mobile: renders a single item at a time with swipe navigation and a
 *   Material UI `MobileStepper`.
 */
import React, { useState } from 'react';
import {
  WhySectionListCustomisation,
  WhySectionListItemCustomisation
} from '../../../common/CommonTypes';
import { CommonValidation } from '../../../common/CommonValidation/commonValidation';
import { useSwipeable } from 'react-swipeable';
import TabPanel from '../../../common/TabPanel/TabPanel';
import MobileStepper from '@mui/material/MobileStepper';
import WhyTabItem from './WhyTabItem';
import Button from '@mui/material/Button';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';

type WhyTabPanelProps = {
  index: number;
  item: WhySectionListCustomisation;
  tabSelected: string;
};

const WhyTabPanel = ({
  index,
  item,
  tabSelected
}: WhyTabPanelProps): JSX.Element => {
  const [activeStep, setActiveStep] = useState(0);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) =>
      prevActiveStep === 0 ? 0 : prevActiveStep - 1
    );
  };

  const getId = (i: number): string => {
    return `why-header-${i}`;
  };

  const showDisclaimer = () => {
    return (
      CommonValidation.isNotEmpty(item.caveatText) &&
      tabSelected === getId(index)
    );
  };

  const handlers = useSwipeable({
    onSwipedLeft: () => handleBack(),
    onSwipedRight: () => handleNext(),
    swipeDuration: 500,
    preventScrollOnSwipe: true,
    trackMouse: true
  });

  const renderWhyItems = (
    panels: WhySectionListItemCustomisation[],
    activeIndex: number | undefined
  ) => {
    if (activeIndex != undefined) {
      return (
        <WhyTabItem
          panel={panels[activeIndex]}
          index={activeIndex}
          key={`why-item-${activeIndex}`}
        />
      );
    }

    return panels.map((panel, panelIndex) => (
      <WhyTabItem
        panel={panel}
        index={panelIndex}
        key={`why-item-${panelIndex}`}
      />
    ));
  };

  return (
    <div>
      <TabPanel
        target={getId(index)}
        className='tabspacer'
        selectedTab={tabSelected}>
        <>
          <div className='mobile-hide'>
            <div className='flex-row'>
              {renderWhyItems(item.whySectionListItems, undefined)}
            </div>
            {showDisclaimer() && (
              <div className='m2-top text-center'>
                {item.caveatText}
              </div>
            )}
          </div>
          <div className='desktop-hide flex-row'>
            <div {...handlers}>
              {renderWhyItems(item.whySectionListItems, activeStep)}
            </div>
            <MobileStepper
              steps={item.whySectionListItems.length}
              position='static'
              activeStep={activeStep}
              nextButton={
                <Button
                  size='small'
                  onClick={handleNext}
                  disabled={activeStep === item.whySectionListItems.length - 1}>
                  <KeyboardArrowRight />
                </Button>
              }
              backButton={
                <Button
                  size='small'
                  onClick={handleBack}
                  disabled={activeStep === 0}>
                  <KeyboardArrowLeft />
                </Button>
              }
            />
          </div>
        </>
      </TabPanel>
      {showDisclaimer() && (
        <div className='desktop-hide why--item'>{item.caveatText}</div>
      )}
    </div>
  );
};

export default WhyTabPanel;
